import { useState } from "react";
import { api } from "../lib/api";
import { Card, Button, Label, Input, Badge } from "../components/ui";

export function Settings() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleInitSkills = async () => {
    setLoading(true);
    try {
      const result = await api.admin.initSkills();
      showMessage("success", `Initialized ${result.skills_initialized} skills and populated Vectorize index`);
    } catch (err) {
      showMessage("error", err instanceof Error ? err.message : "Failed to initialize skills");
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerFetch = async () => {
    setLoading(true);
    try {
      const result = await api.triggers.fetch();
      showMessage("success", "Fetch cycle completed");
    } catch (err) {
      showMessage("error", err instanceof Error ? err.message : "Failed to trigger fetch");
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerEnrich = async () => {
    setLoading(true);
    try {
      const result = await api.triggers.enrich();
      showMessage("success", `Enriched ${result.enriched} opportunities`);
    } catch (err) {
      showMessage("error", err instanceof Error ? err.message : "Failed to trigger enrichment");
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerMatch = async () => {
    setLoading(true);
    try {
      const result = await api.triggers.match();
      const matched = result.results.filter((r: any) => r.matched).length;
      showMessage("success", `Matched ${matched} of ${result.results.length} opportunities`);
    } catch (err) {
      showMessage("error", err instanceof Error ? err.message : "Failed to trigger matching");
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerPitches = async () => {
    setLoading(true);
    try {
      const result = await api.triggers.pitches();
      showMessage("success", `Generated pitches for ${result.processed} opportunities`);
    } catch (err) {
      showMessage("error", err instanceof Error ? err.message : "Failed to generate pitches");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Configure and manage the opportunity scanner</p>
      </div>

      {message && (
        <div className={`card p-4 ${message.type === "success" ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"}`}>
          {message.text}
        </div>
      )}

      {/* Admin Actions */}
      <Card>
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Admin Actions</h2>
          <p className="text-sm text-slate-500 mt-1">Manual triggers for pipeline operations</p>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <h3 className="font-medium text-slate-900">Initialize Skills</h3>
              <p className="text-sm text-slate-500">Load skill taxonomy into D1 and populate Vectorize index for semantic matching</p>
            </div>
            <Button onClick={handleInitSkills} loading={loading} icon="refresh">
              Initialize
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <h3 className="font-medium text-slate-900">Run Fetch Cycle</h3>
              <p className="text-sm text-slate-500">Manually trigger fetching from all configured sources (RSS, Nominatim, HTML)</p>
            </div>
            <Button onClick={handleTriggerFetch} loading={loading} icon="refresh">
              Fetch Now
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <h3 className="font-medium text-slate-900">Enrich Opportunities</h3>
              <p className="text-sm text-slate-500">Run AI enrichment on new opportunities to extract company info, tech stack, pain points</p>
            </div>
            <Button onClick={handleTriggerEnrich} loading={loading} icon="refresh">
              Enrich
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <h3 className="font-medium text-slate-900">Match & Score</h3>
              <p className="text-sm text-slate-500">Run semantic matching against skill vectors and calculate match scores</p>
            </div>
            <Button onClick={handleTriggerMatch} loading={loading} icon="refresh">
              Match
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <h3 className="font-medium text-slate-900">Generate Pitches</h3>
              <p className="text-sm text-slate-500">Create tailored outreach pitches for matched opportunities</p>
            </div>
            <Button onClick={handleTriggerPitches} loading={loading} icon="refresh">
              Generate
            </Button>
          </div>
        </div>
      </Card>

      {/* Configuration */}
      <Card>
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Configuration</h2>
          <p className="text-sm text-slate-500 mt-1">Current system configuration (read-only in UI)</p>
        </div>
        <div className="p-4 space-y-6">
          <div>
            <h3 className="font-medium text-slate-900 mb-3">Matching Thresholds</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="match-threshold">Match Threshold</Label>
                <Input id="match-threshold" type="number" step="0.05" min="0" max="1" value="0.65" disabled />
                <p className="text-xs text-slate-500 mt-1">Minimum score to generate pitches (configured in wrangler.jsonc)</p>
              </div>
              <div>
                <Label htmlFor="max-opportunities">Max Opportunities Per Run</Label>
                <Input id="max-opportunities" type="number" value="50" disabled />
                <p className="text-xs text-slate-500 mt-1">Maximum opportunities to process per scheduled run</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-slate-900 mb-3">Salary Constraints</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="min-salary">Minimum (RM)</Label>
                <Input id="min-salary" type="number" value="5000" disabled />
              </div>
              <div>
                <Label htmlFor="preferred-min">Preferred Min (RM)</Label>
                <Input id="preferred-min" type="number" value="8000" disabled />
              </div>
              <div>
                <Label htmlFor="preferred-max">Preferred Max (RM)</Label>
                <Input id="preferred-max" type="number" value="12000" disabled />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-medium text-slate-900 mb-3">Location Preferences</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="preferred-location">Preferred Location</Label>
                <Input id="preferred-location" value="Selangor, Malaysia" disabled />
              </div>
              <div>
                <Label htmlFor="remote-ok">Remote OK</Label>
                <Input id="remote-ok" type="checkbox" checked disabled />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Sources Configuration */}
      <Card>
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Data Sources</h2>
          <p className="text-sm text-slate-500 mt-1">Configured sources for opportunity discovery</p>
        </div>
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">URL</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <tr><td className="px-4 py-3">JobStreet Malaysia</td><td className="px-4 py-3"><Badge className="bg-blue-50 text-blue-700">RSS</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">jobstreet.com.my/rss/...</td></tr>
                <tr><td className="px-4 py-3">Indeed Malaysia</td><td className="px-4 py-3"><Badge className="bg-blue-50 text-blue-700">RSS</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">malaysia.indeed.com/rss/...</td></tr>
                <tr><td className="px-4 py-3">Maukerja</td><td className="px-4 py-3"><Badge className="bg-blue-50 text-blue-700">RSS</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">maukerja.my/rss/...</td></tr>
                <tr><td className="px-4 py-3">MDEC Grants</td><td className="px-4 py-3"><Badge className="bg-purple-50 text-purple-700">HTML</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">mdec.my/grants</td></tr>
                <tr><td className="px-4 py-3">Cradle Fund</td><td className="px-4 py-3"><Badge className="bg-purple-50 text-purple-700">HTML</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">cradlefund.my</td></tr>
                <tr><td className="px-4 py-3">OSM - CNC Companies</td><td className="px-4 py-3"><Badge className="bg-amber-50 text-amber-700">Nominatim</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">nominatim.openstreetmap.org/...</td></tr>
                <tr><td className="px-4 py-3">OSM - Automation</td><td className="px-4 py-3"><Badge className="bg-amber-50 text-amber-700">Nominatim</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">nominatim.openstreetmap.org/...</td></tr>
                <tr><td className="px-4 py-3">OSM - IoT Companies</td><td className="px-4 py-3"><Badge className="bg-amber-50 text-amber-700">Nominatim</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">nominatim.openstreetmap.org/...</td></tr>
                <tr><td className="px-4 py-3">OSM - Medical Devices</td><td className="px-4 py-3"><Badge className="bg-amber-50 text-amber-700">Nominatim</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">nominatim.openstreetmap.org/...</td></tr>
                <tr><td className="px-4 py-3">JPA Scholarships</td><td className="px-4 py-3"><Badge className="bg-purple-50 text-purple-700">HTML</Badge></td><td className="px-4 py-3"><Badge className="bg-green-50 text-green-700">Enabled</Badge></td><td className="px-4 py-3 text-sm text-slate-500 truncate max-w-xs">jpa.gov.my/scholarships</td></tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-4">Sources are configured in <code className="bg-slate-100 px-1.5 py-0.5 rounded">src/config/constants.ts</code>. Redeploy worker after changes.</p>
        </div>
      </Card>

      {/* Environment Info */}
      <Card>
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Environment</h2>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Worker Name</span>
            <p className="font-mono text-slate-900">opportunity-scanner</p>
          </div>
          <div>
            <span className="text-slate-500">Dashboard</span>
            <p className="font-mono text-slate-900">opportunity-scanner-dashboard</p>
          </div>
          <div>
            <span className="text-slate-500">D1 Database</span>
            <p className="font-mono text-slate-900">opportunity-scanner-db</p>
          </div>
          <div>
            <span className="text-slate-500">Vectorize Index</span>
            <p className="font-mono text-slate-900">skills-index</p>
          </div>
          <div>
            <span className="text-slate-500">Queues</span>
            <p className="font-mono text-slate-900">opportunities-raw, opportunities-enriched, pitches-to-review</p>
          </div>
          <div>
            <span className="text-slate-500">Cron Schedule</span>
            <p className="font-mono text-slate-900">Every 6 hours (0 */6 * * *)</p>
          </div>
        </div>
      </Card>
    </div>
  );
}